import { useQuery } from '@tanstack/react-query';

import { useHttpClient } from '~/hooks/useHttpClient';
import { ReviewEnvelope, ReviewMeetingStatusInfo, ReviewBlock } from '~/services/types';
import { determineRetryAndMaybeLog } from '~/utils/errors';
import { createLogger } from '~/utils/logging';

import { useReviewParams } from './useReviewParams';

const log = createLogger('useReviewClassSummaryQuery');

export const QUERY_KEY = 'reviewClassSummary';

export type ReviewClassSummaryMeeting = ReviewMeetingStatusInfo & {
  markdown: string;
  blocks: ReviewBlock[];
};

export type ReviewClassSummaryResponse = ReviewEnvelope<ReviewClassSummaryMeeting>;

export function useReviewClassSummary({
  date,
  periodId,
  enabled = false,
}: {
  date: Date;
  periodId: string;
  enabled?: boolean;
}) {
  const httpClient = useHttpClient();
  const params = useReviewParams({ date, periodId });

  const paramsQueryStr = params.toQueryString();

  const { data, error, status, refetch } = useQuery<null | ReviewClassSummaryResponse>({
    queryKey: [QUERY_KEY, paramsQueryStr],
    queryFn: async () => {
      log.debug('Fetching review class summary', {
        day: params.day,
        periodId: params.periodId,
        timeZone: params.timeZone ?? 'unknown',
      });
      const response = await httpClient.get<ReviewClassSummaryResponse>(`/review/getClassSummary?${paramsQueryStr}`);
      log.info('Fetched review class summary', {
        meetingCount: response.meetings.length,
      });
      return response;
    },
    retry: (failureCount, error) =>
      determineRetryAndMaybeLog({ operation: 'fetch review class summary', failureCount, error, log }),
    enabled: httpClient.isAuthenticated && enabled,
  });

  return { data, error, status, refetch };
}
